import {
  LuActivity,
  LuAlarmClock,
  LuBookOpen,
  LuClipboardCheck,
  LuCreditCard,
  LuCode,
  LuHeadphones,
  LuKey,
  LuLineChart,
  LuListOrdered,
  LuRadioTower,
  LuSettings,
  LuUser,
  LuUsers,
  LuWorkflow,
} from "react-icons/lu";

import { FaFacebookMessenger, FaSlack, FaWhatsapp } from "react-icons/fa";
import { SiTwilio } from "react-icons/si";
import { LuGlobe } from "react-icons/lu";
import { NAV_SECTIONS, NAV_SECTION_ORDER } from "../../consts/nav-sections";

export const AGENTS_APP_INDEX = 1000;

/**
 * Sidebar sections are declared in `consts/nav-sections` and re-exported here
 * so existing importers of this module keep working.
 *
 * Items carrying the same `section` render under one heading in the left rail,
 * in `NAV_SECTION_ORDER`. An item with no `section` falls back to `BUILD`, so
 * adding a nav entry never silently drops it from the rail.
 */
export { NAV_SECTIONS, NAV_SECTION_ORDER };

const userProfileEndpoint = {
  name: "User Profile",
  href: "/user-settings/profile",
  active: false,
  icon: <LuUser />,
  section: NAV_SECTIONS.ACCOUNT,
};

const billingEndpoint = {
  name: "Subscription",
  href: "/user-settings/subscription",
  active: false,
  icon: <LuCreditCard />,
  section: NAV_SECTIONS.ACCOUNT,
};

const accountAccessKeyEndpoint = {
  name: "Access Keys",
  href: "/account/access-keys",
  active: false,
  icon: <LuKey />,
  section: NAV_SECTIONS.ACCOUNT,
};

const usersEndpoint = {
  name: "Users",
  href: "/account/users",
  active: false,
  icon: <LuUsers />,
  section: NAV_SECTIONS.ACCOUNT,
};

export const userSettingsNavigation = [
  userProfileEndpoint,
  billingEndpoint,
  accountAccessKeyEndpoint,
  usersEndpoint,
];

export const appsNavigation = [];

// default sidebar paths
export const appNavigation = [
  {
    name: "Playbooks",
    href: "/playbooks",
    active: false,
    icon: <LuListOrdered />,
    tip: "Step-by-step procedure editor",
    section: NAV_SECTIONS.BUILD,
  },
  {
    name: "Workflows",
    href: "/journey",
    active: false,
    icon: <LuWorkflow />,
    tip: "Agent flow editor",
    section: NAV_SECTIONS.BUILD,
  },
  {
    name: "Data Sources",
    icon: <LuBookOpen />,
    href: "/contents",
    tip: "Documents and data the agent can draw answers from",
    section: NAV_SECTIONS.BUILD,
    children: [
      { name: "Knowledge", href: "/knowledge-base/docs/data-sources", icon: <LuBookOpen /> },
      { name: "Functions", href: "/functions", icon: <LuCode /> },
    ],
  },
  {
    name: "Channels",
    href: "/channels",
    active: false,
    icon: <LuRadioTower />,
    tip: "Various platforms where you can deploy your agent.",
    section: NAV_SECTIONS.CONNECT,
    // Rendered as a nested group in the rail (see MenuItemGroup).
    children: [
      { name: "Web Chat", href: "/channels/web", icon: <LuGlobe /> },
      { name: "Facebook Messenger", href: "/channels/fb", icon: <FaFacebookMessenger /> },
      { name: "Twilio", href: "/channels/twilio", icon: <SiTwilio /> },
      { name: "Whatsapp", href: "/channels/whatsapp", icon: <FaWhatsapp /> },
      { name: "Slack", href: "/channels/slack", icon: <FaSlack /> },
    ],
  },
  {
    name: "Conversations",
    href: "/hitl",
    active: false,
    icon: <LuHeadphones />,
    tip: "Human-in-the-Loop",
    section: NAV_SECTIONS.CONNECT,
  },
  {
    name: "Analytics",
    href: "/analytics",
    active: false,
    icon: <LuLineChart />,
    tip: "Analyzing Conversations for Deeper Insights",
    section: NAV_SECTIONS.OBSERVE,
  },
  {
    name: "Trace",
    href: "/traces",
    active: false,
    icon: <LuActivity />,
    tip: "Inspect runtime trace events recorded for each conversation session",
    section: NAV_SECTIONS.OBSERVE,
  },
  {
    name: "Agent Tests",
    href: "/evaluation",
    active: false,
    icon: <LuClipboardCheck />,
    tip: "Generate a test plan from this agent's configuration and run it against the live agent",
    section: NAV_SECTIONS.OBSERVE,
  },
  {
    name: "Schedulers",
    href: "/schedulers",
    active: false,
    icon: <LuAlarmClock />,
    tip: "Schedule workflows to run automatically",
    section: NAV_SECTIONS.CONFIGURE,
  },
  {
    name: "Settings",
    href: "/settings",
    active: false,
    icon: <LuSettings />,
    tip: "Set up your agent's basic details, create API keys, and manage live agent transfers.",
    section: NAV_SECTIONS.CONFIGURE,
  },
];

// bottom of the sidebar paths
export const appNavigationEnd = [];

/**
 * Group a flat navigation list into ordered sections, preserving item order
 * inside each one and dropping sections that ended up empty.
 *
 * Each returned item keeps `index`, its position in the original flat list,
 * because the sidebar's active-item lookup is keyed on that.
 *
 * @param {Array<object>} navigation flat nav items, each optionally `section`ed
 * @returns {Array<{section: string, items: Array<object>}>}
 */
export function groupNavigationBySection(navigation = []) {
  const buckets = new Map(NAV_SECTION_ORDER.map((section) => [section, []]));

  navigation.forEach((item, index) => {
    const section = buckets.has(item.section)
      ? item.section
      : NAV_SECTIONS.BUILD;
    buckets.get(section).push({ ...item, index });
  });

  return NAV_SECTION_ORDER.filter(
    (section) => buckets.get(section).length > 0
  ).map((section) => ({ section, items: buckets.get(section) }));
}
